/**
 * Kale links in platte tekst klikbaar maken (ballonnen, transcript, mail
 * zonder html). Alleen http://, https:// en www.; www. krijgt https://.
 * Leestekens aan het eind (.,;:!?)"') horen niet bij de link, een sluit-
 * haakje wel als er in de link een openhaakje staat.
 *
 * De uitkomst is React: tekst blijft tekst (React ontsnapt hem), er gaat
 * nooit iets via innerHTML. "javascript:" of "<script>" blijft dus
 * gewone tekst.
 */
import type { ReactNode } from "react";

export type TekstDeel = { soort: "tekst"; tekst: string } | { soort: "link"; tekst: string; href: string };

// Niet midden in een woord, e-mailadres of pad beginnen ("xwww.", "a@www.").
const KALE_LINK = String.raw`(?<![\w@./-])(?:https?:\/\/|www\.)[^\s<>"]+`;
// [naam](url): url is http(s), www. of een pad op onze eigen shop ("/cart/").
const MARKDOWN_LINK = String.raw`\[([^\]\n]{1,200})\]\(\s*((?:https?:\/\/|www\.)[^\s<>")]+|\/[^\s<>")]*)\s*\)`;
const KANDIDAAT = new RegExp(KALE_LINK, "gi");
const KANDIDAAT_MET_MARKDOWN = new RegExp(`${MARKDOWN_LINK}|${KALE_LINK}`, "gi");
const SHOP = "https://www.voeronline.nl";
const EIND = new Set([".", ",", ";", ":", "!", "?", ")", '"', "'"]);

function tel(tekst: string, teken: string): number {
  return tekst.split(teken).length - 1;
}

/** Knip leestekens van het eind; een haakje blijft als het een paar vormt. */
function knipEind(link: string): string {
  let uit = link;
  while (uit.length > 0) {
    const laatste = uit[uit.length - 1];
    if (!EIND.has(laatste)) break;
    if (laatste === ")" && tel(uit, "(") >= tel(uit, ")")) break;
    uit = uit.slice(0, -1);
  }
  return uit;
}

/** Heeft de link meer dan alleen het begin ("https://" of "www.")? */
function heeftInhoud(link: string): boolean {
  const rest = link.replace(/^(?:https?:\/\/)?(?:www\.)?/i, "");
  return /[\w-]/.test(rest);
}

/**
 * Splits platte tekst in stukken tekst en links. Met `markdown` wordt ook
 * [naam](url) een link met `naam` als tekst; alleen voor berichten van
 * onszelf (Nova, Sanne, JP), nooit voor klanttekst: een klant mag het
 * label van een link niet kunnen kiezen.
 */
export function vindLinks(tekst: string, opties: { markdown?: boolean } = {}): TekstDeel[] {
  const delen: TekstDeel[] = [];
  let vanaf = 0;
  const kandidaat = opties.markdown ? KANDIDAAT_MET_MARKDOWN : KANDIDAAT;
  for (const treffer of tekst.matchAll(kandidaat)) {
    const begin = treffer.index ?? 0;
    if (treffer[1] !== undefined && treffer[2] !== undefined) {
      const doel = treffer[2];
      // "[x](www.)" is geen link: dezelfde inhoudscontrole als bij kale links.
      if (!heeftInhoud(doel)) continue;
      if (begin > vanaf) delen.push({ soort: "tekst", tekst: tekst.slice(vanaf, begin) });
      const href = doel.startsWith("/") ? `${SHOP}${doel}` : /^www\./i.test(doel) ? `https://${doel}` : doel;
      delen.push({ soort: "link", tekst: treffer[1].trim(), href });
      vanaf = begin + treffer[0].length;
      continue;
    }
    const link = knipEind(treffer[0]);
    if (!heeftInhoud(link)) continue;
    if (begin > vanaf) delen.push({ soort: "tekst", tekst: tekst.slice(vanaf, begin) });
    const href = /^www\./i.test(link) ? `https://${link}` : link;
    delen.push({ soort: "link", tekst: link, href });
    vanaf = begin + link.length;
  }
  if (vanaf < tekst.length) delen.push({ soort: "tekst", tekst: tekst.slice(vanaf) });
  return delen;
}

/** Platte tekst als React-nodes, met klikbare links (nieuw tabblad). */
export function metLinks(tekst: string, opties: { markdown?: boolean } = {}): ReactNode[] {
  return vindLinks(tekst, opties).map((deel, i) =>
    deel.soort === "tekst" ? (
      deel.tekst
    ) : (
      <a key={i} href={deel.href} target="_blank" rel="noopener noreferrer" className="v6-link">
        {deel.tekst}
      </a>
    ),
  );
}
